import type { IVector3 } from '@seddi/math-v2';
import { BufferAttribute, BufferGeometry, DynamicDrawUsage, Vector3 } from '@seddi/three';
import type { VertexDataDesc } from './bufferTypes';

/**
 * Create indices buffer.
 * @param self - Geometry.
 * @param data - Indices data.
 */
export const createIndex = (self: BufferGeometry, data: Readonly<Uint32Array>) => {
  const attribute = new BufferAttribute(data, 1);
  self.setIndex(attribute);
};

/**
 * Updates indices buffer.
 * @param self - Geometry.
 * @param data - Indices.
 */
export const updateIndex = (self: BufferGeometry, data: Readonly<Uint32Array>) => {
  // NOTE: Currently we cant reuse the previous buffer
  // Chrome 96 seems to be incompatible with IndexBuffer DYNAMIC_DRAW
  // let attribute: BufferAttribute = self.index;
  // attribute.setUsage(DynamicDrawUsage);
  const attribute = new BufferAttribute(data, 1);
  self.setIndex(attribute);
};

/**
 * Create vertex attribute.
 * @param self - Geometry.
 * @param key - Key.
 * @param data - Data.
 * @param itemSize - Item size.
 */
export const createVertexAttributeF32 = (
  self: BufferGeometry,
  key: string,
  data: Readonly<Float32Array>,
  itemSize: number,
) => {
  const attribute = new BufferAttribute(data, itemSize);
  self.setAttribute(key, attribute);
};

/**
 * Update vertex attribute reusing previous buffer if possible.
 * @param self - Geometry.
 * @param key - Key.
 * @param data - Data.
 * @param itemSize - Item size.
 */
export const updateVertexAttributeF32 = (
  self: BufferGeometry,
  key: string,
  data: Readonly<Float32Array>,
  itemSize: number,
) => {
  let attribute: BufferAttribute = self.getAttribute(key) as BufferAttribute;

  if (!attribute || attribute.array.length !== data.length) {
    // Recreate.
    // data = new Float32Array(data);
    attribute = new BufferAttribute(data, itemSize);
    attribute.setUsage(DynamicDrawUsage);
    self.setAttribute(key, attribute);
  } else {
    // Update in-place, do not use copyArray as it violates immutability of state.
    attribute.array = data;
  }

  attribute.needsUpdate = true;
};

/**
 * Recompute vertex normals reusing previous buffer if possible.
 * @param self - Geometry.
 */
export const computeVertexNormalsF32 = (self: BufferGeometry) => {
  const positionAttribute = self.getAttribute('position');
  const positions = positionAttribute.array as Float32Array;

  let normalAttribute = self.getAttribute('normal');
  let normals;
  if (!normalAttribute || normalAttribute.array.length !== positionAttribute.array.length) {
    normalAttribute = new BufferAttribute(new Float32Array(positions.length), 3);
    normals = normalAttribute.array as Float32Array;
    self.setAttribute('normal', normalAttribute);
  } else {
    // Reuse existing attribute.
    normals = normalAttribute.array as Float32Array;
    normals.fill(0);
  }

  const pA = new Vector3();
  const pB = new Vector3();
  const pC = new Vector3();
  const cb = new Vector3();
  const ab = new Vector3();

  // indexed elements
  const indices = self.index.array as Uint32Array;

  for (let i = 0, il = indices.length; i < il; i += 3) {
    const oA = indices[i + 0] * 3;
    const oB = indices[i + 1] * 3;
    const oC = indices[i + 2] * 3;

    pA.fromArray(positions, oA);
    pB.fromArray(positions, oB);
    pC.fromArray(positions, oC);

    cb.subVectors(pC, pB);
    ab.subVectors(pA, pB);
    cb.cross(ab);

    addToArray3(normals, oA, cb);
    addToArray3(normals, oB, cb);
    addToArray3(normals, oC, cb);
  }

  // Normalize normals.
  for (let i = 0, il = normals.length; i < il; i += 3) {
    cb.fromArray(normals, i);
    cb.normalize();
    cb.toArray(normals, i);
  }

  normalAttribute.needsUpdate = true;
};

/**
 * Create buffer geometry.
 * @param data - Geometry data.
 */
export const create = (
  data: VertexDataDesc,
  options?: { computeBoundingSphere?: boolean },
): BufferGeometry => {
  const self = new BufferGeometry();

  const indexAttribute = new BufferAttribute(data.indices, 1);
  self.setIndex(indexAttribute);
  const positionAttribute = new BufferAttribute(data.positions, 3);
  self.setAttribute('position', positionAttribute);

  if (data.normals) {
    const normalAttribute = new BufferAttribute(data.normals, 3);
    self.setAttribute('normal', normalAttribute);
  } else {
    computeVertexNormalsF32(self);
  }

  if (data.uvs) {
    const uvAttribute = new BufferAttribute(data.uvs, 2);
    self.setAttribute('uv', uvAttribute);
  }

  // Recompute bounding sphere at runtime
  if (options?.computeBoundingSphere) {
    self.computeBoundingSphere();
  }

  return self;
};

const addToArray3 = (array: Float32Array, offset: number, value: IVector3) => {
  array[offset] += value.x;
  array[offset + 1] += value.y;
  array[offset + 2] += value.z;
};
